"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * usePageLoading Hook
 *
 * Custom hook to track page loading state and integrate with NextTopLoader.
 * Provides a clean interface for components that need to respond to navigation events.
 *
 * @returns {Object} Loading state and utilities
 */
export const usePageLoading = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const pathname = usePathname();
  const isUnmountingRef = useRef(false);

  // Check if we're on an auth page
  const isAuthPage =
    pathname?.startsWith("/signup") ||
    pathname?.startsWith("/login") ||
    pathname?.startsWith("/reset-password");

  useEffect(() => {
    let animationTimeout: ReturnType<typeof setTimeout> | undefined;
    let loadingTimeout: ReturnType<typeof setTimeout> | undefined;
    let htmlObserver: MutationObserver | null = null;
    let bodyObserver: MutationObserver | null = null;

    const clearAnimationTimeout = () => {
      if (animationTimeout !== undefined) {
        clearTimeout(animationTimeout);
        animationTimeout = undefined;
      }
    };

    const clearLoadingTimeout = () => {
      if (loadingTimeout !== undefined) {
        clearTimeout(loadingTimeout);
        loadingTimeout = undefined;
      }
    };

    // Function to trigger animation on button click
    const triggerAnimation = () => {
      clearAnimationTimeout();
      clearLoadingTimeout();
      setIsLoading(true);
      setIsAnimating(true);

      // Stop animation after 2 seconds
      animationTimeout = setTimeout(() => {
        setIsAnimating(false);
      }, 2000);

      // Stop loading state after 3 seconds
      loadingTimeout = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    };

    // Global button click handler
    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as Element;

      // Find the closest interactive element (button, anchor, or element with role="button", role="menuitem" or radix item)
      const button = target.closest(
        'button, a[href], [role="button"], [role="menuitem"], [data-radix-collection-item]'
      ) as HTMLElement;

      if (button) {
        // Skip if element is disabled
        const isDisabled =
          button.hasAttribute("disabled") ||
          button.hasAttribute("data-disabled") ||
          button.classList.contains("disabled") ||
          button.getAttribute("aria-disabled") === "true" ||
          (button instanceof HTMLAnchorElement && !button.href);

        // Skip tour navigation buttons to prevent animation when tour completes
        const isTourButton = button.closest('[data-tour-elem]');

        // Trigger animation for all enabled button clicks (except tour buttons)
        if (!isDisabled && !isTourButton) {
          triggerAnimation();
        }
      }
    };

    const handleStart = () => {
      // Don't start animation on auth pages
      if (isAuthPage) {
        return;
      }
      setIsLoading(true);
      setIsAnimating(true);
      clearAnimationTimeout();
      clearLoadingTimeout();
      animationTimeout = setTimeout(() => setIsAnimating(false), 2000);
    };

    const handleComplete = () => {
      setIsLoading(false);
      clearAnimationTimeout();
      // Keep animating briefly after completion to avoid jank
      loadingTimeout = setTimeout(() => setIsAnimating(false), 300);
    };

    const syncFromDom = () => {
      // Don't auto-trigger animation on auth pages (only prevent automatic triggers)
      if (isAuthPage) {
        // On auth pages, only reset if we detect complete (not busy)
        const isBusy =
          document.documentElement.classList.contains("nprogress-busy") ||
          !!document.getElementById("nprogress");
        if (!isBusy) {
          // Only reset if page is fully loaded (allow explicit triggers to work)
          handleComplete();
        }
        return;
      }
      const isBusy =
        document.documentElement.classList.contains("nprogress-busy") ||
        !!document.getElementById("nprogress");
      if (isBusy) {
        handleStart();
      } else {
        handleComplete();
      }
    };

    // Initial sync in case loader is already active
    // Delay initial sync to ensure DOM is ready
    const initialSyncTimeout = setTimeout(() => {
      syncFromDom();
      // If no loading state detected, ensure animation is reset
      const isBusy =
        document.documentElement.classList.contains("nprogress-busy") ||
        !!document.getElementById("nprogress");
      if (!isBusy) {
        setIsLoading(false);
        setIsAnimating(false);
      }
    }, 100);

    // Observe <html> class changes and #nprogress presence
    htmlObserver = new MutationObserver(syncFromDom);
    htmlObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    bodyObserver = new MutationObserver(syncFromDom);
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    // Listen to nextjs-toploader custom events
    if (typeof window !== "undefined") {
      window.addEventListener("nextjs-toploader:start", handleStart);
      window.addEventListener("nextjs-toploader:complete", handleComplete);

      // Listen for button clicks globally to trigger animation
      document.addEventListener("click", handleButtonClick, true);
    }

    return () => {
      if (htmlObserver) htmlObserver.disconnect();
      if (bodyObserver) bodyObserver.disconnect();
      if (typeof window !== "undefined") {
        window.removeEventListener("nextjs-toploader:start", handleStart);
        window.removeEventListener("nextjs-toploader:complete", handleComplete);
        document.removeEventListener("click", handleButtonClick, true);
      }
      clearTimeout(initialSyncTimeout);
      clearAnimationTimeout();
      clearLoadingTimeout();
      if (!isUnmountingRef.current) {
        setIsAnimating(false);
        setIsLoading(false);
      }
    };
  }, [isAuthPage]);

  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  return {
    isLoading,
    isAnimating,
    setIsLoading,
    setIsAnimating,
  };
};
